/**
 * TanStack React Query key factory i hook-ovi za admin dashboard overview API.
 * Kombinuje dashboard/overview/search sa CSO i public-body listama za obogaćeni view model.
 */

import { useQuery } from '@tanstack/react-query';
import DashboardOverviewService from '@/shared/services/DashboardOverviewService';
import CSOApplicationService from '@/shared/services/cso/CSOApplicationService';
import CSOProfileService from '@/shared/services/cso/CSOProfileService';
import PublicBodyService from '@/shared/services/cso/PublicBodyService';
import { mapDashboardOverviewToViewModel } from '@/features/admin/workspace/utils/mapDashboardOverviewResponse';
import { mergeOrganizationLists } from '@/shared/notifications/organizationNotificationUtils';

/** Query key factory za dashboard overview keš. */
export const dashboardOverviewKeys = {
  all: ['dashboardOverview'],
  overview: (notificationLimit) => [...dashboardOverviewKeys.all, notificationLimit],
};

const STALE_TIME_MS = 60 * 1000;

/** Broj stavki za preview u dashboard koloni (BE parametar: notification_limit). */
export const DASHBOARD_OVERVIEW_NOTIFICATION_LIMIT = 10;

/**
 * Fetch dashboard overview podataka obogaćenih listom organizacija (CSO + javna tela).
 *
 * @param {object} [options]
 * @param {number} [options.notificationLimit] - Limit notifikacija za preview (podrazumevano 10).
 * @returns {Promise<object>} Mapirani view model spreman za prikaz.
 */
export async function fetchDashboardOverview({ notificationLimit = DASHBOARD_OVERVIEW_NOTIFICATION_LIMIT } = {}) {
  const raw = await DashboardOverviewService.search({ notification_limit: notificationLimit });

  let organizations = [];
  try {
    const [csoApproval, csoVerified, pbApproval, pbVerified] = await Promise.all([
      CSOApplicationService.getApprovalList(),
      CSOProfileService.getAllOrganizations(),
      PublicBodyService.getApprovalListPublicBodies(),
      PublicBodyService.getAllPublicBodies(),
    ]);
    organizations = mergeOrganizationLists(csoApproval, csoVerified, pbApproval, pbVerified);
  } catch {
    organizations = [];
  }

  return mapDashboardOverviewToViewModel(raw, { organizations });
}

/**
 * React Query hook za admin dashboard overview.
 *
 * @param {import('@tanstack/react-query').UseQueryOptions & { notificationLimit?: number }} [options] - Dodatne opcije; `notificationLimit` override-uje podrazumevani limit.
 * @returns {import('@tanstack/react-query').UseQueryResult<object>}
 */
export function useDashboardOverviewQuery(options = {}) {
  const { notificationLimit, ...queryOptions } = options;
  const limit = notificationLimit ?? DASHBOARD_OVERVIEW_NOTIFICATION_LIMIT;
  return useQuery({
    queryKey: dashboardOverviewKeys.overview(limit),
    queryFn: () => fetchDashboardOverview({ notificationLimit: limit }),
    staleTime: STALE_TIME_MS,
    ...queryOptions,
  });
}
